"use client";

/**
 * AlertCellModal — expanded node view.
 *
 * Three tabs: Summary (plain-English Gemini markdown, cached once forever),
 * Code (raw cell source), Output (same renderers as NotebookCell, via
 * AlertCellOutputsView). No Regenerate button — killed per user.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  FileText,
  Code2,
  TerminalSquare,
  Copy,
  Check,
  Bell,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  CircleDot,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { API_BASE_URL } from "@/lib/config";
import {
  ICY,
  TITLE_CLASS,
  SUB_CLASS,
  SPRING_IN,
} from "@/lib/alert-theme";
import {
  useAlertCellModal,
} from "./AlertCellModalContext";
import { AlertCellOutputsView } from "./AlertCellOutputsView";
import { isDashboardOutput } from "@/lib/alert-output-detection";
import { Markdown } from "@/components/markdown";
import { CodeBlock } from "@/components/notebook/CodeBlock";

type TabKey = "summary" | "code" | "output";

const TABS: { key: TabKey; icon: ReactNode; label: string }[] = [
  { key: "summary", icon: <FileText size={13} />, label: "Summary" },
  { key: "code", icon: <Code2 size={13} />, label: "Code" },
  { key: "output", icon: <TerminalSquare size={13} />, label: "Output" },
];

/** Tick-only status dot — no "RAN CLEANLY" text per user feedback. */
function StatusDot({
  status,
  viewMode,
}: {
  status: "idle" | "pending" | "running" | "success" | "error";
  viewMode?: string;
}) {
  if (status === "success") {
    return (
      <span
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-400/15 text-emerald-300"
        aria-label="Ran cleanly"
        title="Ran cleanly"
      >
        <CheckCircle2 className="w-3 h-3" strokeWidth={2.4} />
      </span>
    );
  }
  if (status === "error") {
    return (
      <span
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-400/15 text-red-300"
        aria-label="Errored"
        title="Errored"
      >
        <AlertTriangle className="w-3 h-3" strokeWidth={2.4} />
      </span>
    );
  }
  if (status === "running") {
    return (
      <Loader2
        className={`w-3.5 h-3.5 animate-spin ${viewMode === "signal" ? "text-emerald-200" : "text-sky-200"}`}
        aria-label="Running"
      />
    );
  }
  return (
    <CircleDot
      className="w-3.5 h-3.5 text-slate-400"
      aria-label={status === "pending" ? "Pending" : "Queued"}
    />
  );
}

export function AlertCellModal({
  sessionId: sessionIdProp,
  viewMode,
}: { sessionId?: string; viewMode?: string } = {}) {
  const {
    isOpen,
    data,
    closeModal,
    getCachedExplanation,
    setCachedExplanation,
  } = useAlertCellModal();
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("summary");
  const [copyOk, setCopyOk] = useState(false);

  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const prevFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    // Respect the `initialTab` set by the caller (hero node passes "output"
    // so the dashboard renders immediately). Falls back to "summary" for
    // every other node — same behavior as before.
    setActiveTab(data?.initialTab ?? "summary");
    setCopyOk(false);
  }, [isOpen, data?.cellIndex, data?.initialTab]);

  useEffect(() => {
    if (!isOpen) return;
    prevFocusRef.current = document.activeElement;
    const t = setTimeout(() => closeBtnRef.current?.focus(), 10);
    return () => {
      clearTimeout(t);
      const prev = prevFocusRef.current as HTMLElement | null;
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closeModal]);

  const sessionId = data?.sessionId || sessionIdProp || "";

  // Dashboard cells deserve a much wider modal — the final-results dashboard
  // is a rich HTML layout that reads as squished inside the default 3xl (768px)
  // shell. When ANY output on this cell is a dashboard, swap to a near-full
  // width container so tables, metric tiles, and multi-column grids can
  // breathe.
  const hasDashboard =
    Array.isArray(data?.outputs) &&
    data!.outputs!.some((o) => isDashboardOutput(o as any));
  const shellWidthClass = hasDashboard
    ? "w-full max-w-[min(1400px,96vw)] mx-4 max-h-[92vh]"
    : "w-full max-w-3xl mx-4 max-h-[86vh]";

  const handleCopy = useCallback(async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.code || "");
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1200);
    } catch {
      /* ignore */
    }
  }, [data]);

  if (!mounted) return null;

  const content = (
    <AnimatePresence>
      {isOpen && data && (
        <motion.div
          key="alert-cell-modal"
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={closeModal}
          aria-modal="true"
          role="dialog"
        >
          {/* Neutral dark backdrop — the old slate-950 tint read as "blue hue"
              on top of a white-ish dashboard iframe. Swapped to pure black
              at 70% so the dashboard colors come through unchanged. */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          <motion.div
            initial={SPRING_IN.initial}
            animate={SPRING_IN.animate}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{
              type: "spring",
              stiffness: 280,
              damping: 26,
              mass: 0.7,
              duration: 0.22,
            }}
            className={cn(
              "relative z-10 flex flex-col overflow-hidden rounded-lg",
              shellWidthClass,
              "bg-slate-950 border border-slate-400/25 shadow-2xl",
            )}
            onClick={(e) => e.stopPropagation()}
            // Stable hook for the onboarding-buddy tour. Lets a beat
            // anchor its bubble to the modal shell (the
            // `alert_node_modal_explain` beat in the alert-mode tour
            // does this so the explanation reads as part of the modal,
            // not a floating island next to it).
            data-testid="alert-cell-modal"
          >
            {/* Neutral top accent — the old sky-300 gradient tinted the header
                of every open modal blue. Replaced with a soft slate hairline. */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-400/35 to-transparent" />

            {/* Header — compact, inline (icon baseline-aligned with title). */}
            <div className="relative flex items-center gap-3 px-5 py-3 border-b border-slate-400/15">
              <div
                className={cn(
                  "shrink-0 w-7 h-7 rounded-md border flex items-center justify-center",
                  ICY.chipBg,
                )}
              >
                <span
                  className={cn(
                    "text-[10.5px] font-bold tabular-nums leading-none",
                    ICY.chipText,
                  )}
                >
                  #{data.displayIndex}
                </span>
              </div>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className={cn(TITLE_CLASS, "truncate text-[14px]")}>
                  {data.purpose || "Untitled step"}
                </div>
                <StatusDot status={data.status} viewMode={viewMode} />
                {data.hasAlertSend && (
                  <span
                    className={cn(
                      "shrink-0 inline-flex items-center gap-1 rounded-md border px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-wider",
                      ICY.chipBg,
                      ICY.chipText,
                    )}
                  >
                    <Bell className="w-2.5 h-2.5" />
                    sends email
                  </span>
                )}
              </div>
              <button
                ref={closeBtnRef}
                onClick={closeModal}
                aria-label="Close"
                // Stable hook for the onboarding-buddy tour's
                // `alert_node_modal_close` click_target beat — the
                // pulsing green ring + "Click here" pill anchor here
                // so the viewer learns to dismiss the modal and
                // return to the canvas.
                data-testid="alert-cell-modal-close"
                className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-slate-50 hover:bg-slate-800/60 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            {/* Tabs */}
            <div className="relative flex items-center gap-1 px-3 border-b border-slate-400/15 bg-slate-950/60">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 h-9 text-[12px] transition-colors duration-150 border-b-2",
                    activeTab === t.key
                      ? viewMode === "signal" ? "text-emerald-100 border-emerald-300 font-semibold" : "text-sky-100 border-sky-300 font-semibold"
                      : "text-slate-300/80 border-transparent hover:text-slate-100",
                  )}
                >
                  {t.icon}
                  {t.label}
                </button>
              ))}
              <div className="flex-1" />
              {activeTab === "code" && (
                <button
                  onClick={handleCopy}
                  aria-label="Copy code"
                  className={`inline-flex items-center gap-1 h-7 px-2 my-1 rounded-md text-[11px] text-slate-300 transition-colors ${viewMode === "signal" ? "hover:text-emerald-100 hover:bg-emerald-300/10" : "hover:text-sky-100 hover:bg-sky-300/10"}`}
                >
                  {copyOk ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                  {copyOk ? "Copied" : "Copy"}
                </button>
              )}
            </div>

            {/* Body */}
            <div className="relative flex-1 overflow-auto px-5 py-4">
              <AnimatePresence mode="wait">
                {activeTab === "summary" && (
                  <motion.div
                    key="summary"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.16 }}
                  >
                    <SummaryTab
                      cellIndex={data.cellIndex}
                      code={data.code}
                      purpose={data.purpose}
                      sessionId={sessionId}
                      preloadedMarkdown={data.descriptionMd ?? null}
                      getCachedExplanation={getCachedExplanation}
                      setCachedExplanation={setCachedExplanation}
                      viewMode={viewMode}
                    />
                  </motion.div>
                )}
                {activeTab === "code" && (
                  <motion.div
                    key="code"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.16 }}
                    className="rounded-lg border border-slate-400/15 overflow-hidden max-h-[62vh]"
                  >
                    <div className="overflow-auto max-h-[62vh]">
                      <CodeBlock code={data.code || "# (empty step)"} language="python" />
                    </div>
                  </motion.div>
                )}
                {activeTab === "output" && (
                  <motion.div
                    key="output"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.16 }}
                  >
                    <AlertCellOutputsView
                      outputs={data.outputs}
                      stdout={data.stdout}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary tab (was "Description") — tighter markdown spacing.
// ─────────────────────────────────────────────────────────────────────────────

function SummaryTab({
  cellIndex,
  code,
  purpose,
  sessionId,
  preloadedMarkdown,
  getCachedExplanation,
  setCachedExplanation,
  viewMode,
}: {
  cellIndex: number;
  code: string;
  purpose: string;
  sessionId: string;
  viewMode?: string;
  /** Pre-generated markdown from `alertDoc.cell_descriptions` — when
   *  present, this IS the rich description and the modal renders it
   *  immediately without ever hitting /alerts/explain-cell. */
  preloadedMarkdown: string | null;
  getCachedExplanation: (i: number) => string | undefined;
  setCachedExplanation: (i: number, md: string) => void;
}) {
  // Prefer the pre-generated description from the alert doc. Falls back
  // to the in-memory explanation cache (prior modal open this session),
  // and only if BOTH are missing do we hit the /alerts/explain-cell
  // endpoint with a loader.
  const preloaded = preloadedMarkdown && preloadedMarkdown.trim().length > 0
    ? preloadedMarkdown
    : null;
  const cached = preloaded ?? getCachedExplanation(cellIndex);
  const [md, setMd] = useState<string | null>(cached ?? null);
  const [loading, setLoading] = useState<boolean>(!cached);
  const [failed, setFailed] = useState<boolean>(false);

  useEffect(() => {
    // If we already have a preloaded description, short-circuit: store it
    // in the in-memory cache so subsequent opens hit layer 1 (cheap), and
    // render it immediately. No fetch, no loader.
    if (preloaded) {
      setMd(preloaded);
      setLoading(false);
      setFailed(false);
      setCachedExplanation(cellIndex, preloaded);
      return;
    }
    if (cached) {
      setMd(cached);
      setLoading(false);
      setFailed(false);
      return;
    }
    setMd(null);
    setLoading(true);
    setFailed(false);

    let aborted = false;

    async function fetchWithRetry(attempt: number): Promise<void> {
      try {
        const res = await fetch(`${API_BASE_URL}/alerts/explain-cell`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            cell_index: cellIndex,
            code,
            purpose,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (aborted) return;
        if (json?.ok && json?.markdown) {
          setCachedExplanation(cellIndex, json.markdown);
          setMd(json.markdown);
          setLoading(false);
        } else if (attempt < 1) {
          setTimeout(() => {
            if (!aborted) void fetchWithRetry(attempt + 1);
          }, 800);
        } else {
          setFailed(true);
          setLoading(false);
        }
      } catch {
        if (attempt < 1) {
          setTimeout(() => {
            if (!aborted) void fetchWithRetry(attempt + 1);
          }, 800);
        } else if (!aborted) {
          setFailed(true);
          setLoading(false);
        }
      }
    }

    void fetchWithRetry(0);

    return () => {
      aborted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellIndex, code, preloaded]);

  if (loading) {
    return (
      <div className="flex items-center gap-2.5 py-4 text-[12px] text-slate-200">
        <Loader2 className={`w-3.5 h-3.5 animate-spin ${viewMode === "signal" ? "text-emerald-200" : "text-sky-200"}`} />
        Writing a plain-English summary…
      </div>
    );
  }

  if (failed) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-slate-400/20 bg-slate-900/40 px-4 py-3">
        <AlertTriangle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
        <div className={cn(SUB_CLASS, "text-slate-100/85")}>
          Couldn&apos;t generate a summary right now.
        </div>
      </div>
    );
  }

  if (!md) {
    return (
      <div className={cn("flex items-center gap-2", SUB_CLASS)}>
        <CircleDot className="w-3 h-3" />
        No summary available.
      </div>
    );
  }

  // Tight markdown styling — kills the prose airiness, keeps bold/bullets readable.
  return (
    <div className="alert-summary-md text-[13px] leading-[1.55] text-slate-100">
      <Markdown>{md}</Markdown>
      <style jsx>{`
        .alert-summary-md :global(p) {
          margin: 0 0 0.5rem 0;
        }
        .alert-summary-md :global(p:last-child) {
          margin-bottom: 0;
        }
        .alert-summary-md :global(strong) {
          color: rgb(240 249 255);
          font-weight: 600;
        }
        .alert-summary-md :global(ul),
        .alert-summary-md :global(ol) {
          margin: 0.25rem 0 0.25rem 1.1rem;
          padding: 0;
        }
        .alert-summary-md :global(li) {
          margin: 0.1rem 0;
          padding: 0;
          line-height: 1.5;
        }
        .alert-summary-md :global(code) {
          background: rgba(186, 230, 253, 0.1);
          color: rgb(224 242 254);
          padding: 1px 5px;
          border-radius: 4px;
          font-size: 0.94em;
        }
      `}</style>
    </div>
  );
}
